/**
 * useFirstRunActivation
 *
 * Resolves the first-run activation nudge for the current player and decides
 * whether the one-tap free-play interstitial should show.
 *
 * This is now the PRODUCT DEFAULT, not an A/B experiment: the server buckets
 * every eligible first-run player to `treatment` (the winning arm was shipped
 * to 100% and baked in). The client contract is unchanged — the resolve still
 * returns { variant, treatment } and we still only show on `variant ===
 * 'treatment'` — so no client change was needed beyond this note. The nudge has
 * no dependency on the experiment scaffolding existing server-side.
 *
 * Guarantees (all BLOCKER-level per feature doc 22):
 * - FAIL-SAFE: any resolve failure yields control (no modal). The helper never
 *   throws, and every gate below defaults to "no treatment".
 * - NEVER BLOCKS FIRST PAINT: the resolve runs in an effect (post-paint) and
 *   the modal only mounts once a treatment bucket has resolved.
 * - STICKY PER SESSION: the resolved result is cached in sessionStorage keyed
 *   by player id, so navigating around the app never re-calls the endpoint and
 *   the modal never double-fires. Dismissal is remembered for the session too.
 * - SUPPRESSED WHILE GATED: never resolves/renders while the player still needs
 *   DOB (DobGate) or is geo-blocked. The server enforces eligibility as well;
 *   this is defense in depth on the client.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { resolveFirstRun } from "../../../shared/services/experimentApi";

const STORAGE_PREFIX = "cluck.firstRunActivation.v1";

// sessionStorage can throw (private mode, quota, disabled). Every access is
// wrapped so a storage failure degrades to "not cached / not dismissed" rather
// than breaking the lobby.
function readSession(key) {
  if (!key) return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key, value) {
  if (!key) return;
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* ignore storage failures */
  }
}

export function useFirstRunActivation() {
  const { isLoggedIn, user, needsDob, geoBlocked } = useAuth();

  const [treatment, setTreatment] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  // Ensures the resolve runs at most once per mounted session, even across
  // re-renders triggered by auth/gate state settling.
  const startedRef = useRef(false);

  const userId = user?.id || null;
  const resolvedKey = userId ? `${STORAGE_PREFIX}.${userId}.resolved` : null;
  const dismissedKey = userId ? `${STORAGE_PREFIX}.${userId}.dismissed` : null;

  // Hydrate a prior dismissal so a mid-session remount does not re-nag.
  useEffect(() => {
    if (dismissedKey && readSession(dismissedKey) === "1") {
      setDismissed(true);
    }
  }, [dismissedKey]);

  useEffect(() => {
    // Eligibility gate: authenticated, ungated player only. Any failure here
    // simply means no treatment (control behaviour).
    if (!isLoggedIn || !userId || needsDob || geoBlocked) return;
    // Already dismissed this session -> never resolve or show again.
    if (readSession(dismissedKey) === "1") return;
    // At most one resolve per session per player.
    if (startedRef.current) return;

    // Re-hydrate a cached result instead of hitting the network again. Keeps
    // the treatment sticky and flicker-free across navigation within a session.
    const cached = readSession(resolvedKey);
    if (cached) {
      startedRef.current = true;
      try {
        const parsed = JSON.parse(cached);
        if (parsed?.variant === "treatment" && parsed?.treatment?.game_code) {
          setTreatment(parsed.treatment);
        }
      } catch {
        /* corrupt cache -> treat as control */
      }
      return;
    }

    startedRef.current = true;
    let active = true;
    // Fire-and-forget: resolveFirstRun never rejects.
    resolveFirstRun().then((res) => {
      if (!active) return;
      writeSession(resolvedKey, JSON.stringify(res));
      if (res.variant === "treatment" && res.treatment?.game_code) {
        setTreatment(res.treatment);
      }
    });

    return () => {
      active = false;
    };
  }, [isLoggedIn, userId, needsDob, geoBlocked, resolvedKey, dismissedKey]);

  const dismiss = useCallback(() => {
    writeSession(dismissedKey, "1");
    setDismissed(true);
  }, [dismissedKey]);

  // Final render gate. Re-checks the live auth/gate state so the modal can
  // never linger if the player becomes gated after resolve.
  const showTreatment =
    !!treatment &&
    !dismissed &&
    isLoggedIn &&
    !needsDob &&
    !geoBlocked;

  return { showTreatment, treatment, dismiss };
}

export default useFirstRunActivation;
